import { defineStore } from 'pinia';
import { ref, reactive, computed } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useTimezoneStore } from '@/stores/timezone';
import { getAccount, updateGeneralInformation, updateTimezone, uploadAvatar, deleteAvatar } from '@/services/userService';
import { getSessions, signOutSession, type DeviceSession } from '@/services/sessionService';
import { ApiError } from '@/models/errors/ApiError';

export interface AccountSettings {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    timezone: string | null;
    avatarUrl: string;
}

const EMPTY_ACCOUNT: AccountSettings = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    timezone: null,
    avatarUrl: '',
};

export const useAccountStore = defineStore('account', () => {
    const authStore = useAuthStore();
    const tzStore = useTimezoneStore();

    const form = reactive<AccountSettings>({ ...EMPTY_ACCOUNT });
    const originalData = ref<AccountSettings | null>(null);
    const loading = ref(false);
    const saving = ref(false);
    const savingGeneralInfo = ref(false);
    const devices = ref<DeviceSession[]>([]);
    const signingOutDeviceId = ref<number | null>(null);
    const initialized = ref(false);

    // Avatar file handling
    const avatarFile = ref<File | null>(null);
    const previewAvatarUrl = ref('');
    const avatarMarkedForDeletion = ref(false);

    const displayAvatarUrl = computed(() => {
        if (previewAvatarUrl.value) return previewAvatarUrl.value;
        if (avatarMarkedForDeletion.value) return '';
        return form.avatarUrl;
    });

    const isDirty = computed(() => {
        if (!originalData.value) return false;
        const hasFormChange = (Object.keys(EMPTY_ACCOUNT) as (keyof AccountSettings)[])
            .some((key) => form[key] !== originalData.value?.[key]);
        return hasFormChange || avatarFile.value !== null || avatarMarkedForDeletion.value;
    });

    const fullName = computed(() => {
        const first = form.firstName.trim();
        const last = form.lastName.trim();
        return [first, last].filter(Boolean).join(' ');
    });

    function clearAvatarFile() {
        if (previewAvatarUrl.value) {
            URL.revokeObjectURL(previewAvatarUrl.value);
        }
        avatarFile.value = null;
        previewAvatarUrl.value = '';
    }

    function markAvatarForDeletion() {
        clearAvatarFile();
        avatarMarkedForDeletion.value = true;
    }

    function setAvatarFile(file: File) {
        clearAvatarFile();
        avatarMarkedForDeletion.value = false;
        avatarFile.value = file;
        previewAvatarUrl.value = URL.createObjectURL(file);
    }

    function applyData(data: AccountSettings) {
        form.firstName = data.firstName;
        form.lastName = data.lastName;
        form.email = data.email;
        form.phone = data.phone;
        form.timezone = data.timezone;
        form.avatarUrl = data.avatarUrl;
        originalData.value = { ...data };
    }

    async function fetchAccountSettings() {
        if (initialized.value) return;
        loading.value = true;
        try {
            const profile = await getAccount();
            applyData({ ...profile });
            initialized.value = true;
            tzStore.setFromProfile(profile.timezone);
        } finally {
            loading.value = false;
            tzStore.markReady();
        }
    }

    async function saveAccountSettings() {
        saving.value = true;
        try {
            if (form.timezone !== originalData.value?.timezone) {
                const updated = await updateTimezone({
                    timezone: form.timezone,
                });
                applyData({ ...updated });
                tzStore.setFromProfile(updated.timezone);
            }

            if (avatarFile.value) {
                const updated = await uploadAvatar(avatarFile.value);
                clearAvatarFile();
                avatarMarkedForDeletion.value = false;
                applyData({ ...updated });
                tzStore.setFromProfile(updated.timezone);
            } else if (avatarMarkedForDeletion.value && form.avatarUrl) {
                const updated = await deleteAvatar();
                avatarMarkedForDeletion.value = false;
                applyData({ ...updated });
                tzStore.setFromProfile(updated.timezone);
            }
        } finally {
            saving.value = false;
        }
    }

    async function saveGeneralInformation(): Promise<void> {
        savingGeneralInfo.value = true;
        try {
            const updated = await updateGeneralInformation({
                firstName: form.firstName,
                lastName: form.lastName,
                phone: form.phone.replace(/\D/g, ''),
            });
            form.firstName = updated.firstName;
            form.lastName = updated.lastName;
            form.phone = updated.phone;
            if (originalData.value) {
                originalData.value = {
                    ...originalData.value,
                    firstName: updated.firstName,
                    lastName: updated.lastName,
                    phone: updated.phone,
                };
            }
            if (authStore.user) {
                authStore.user = {
                    ...authStore.user,
                    name: [updated.firstName, updated.lastName].filter(Boolean).join(' ') || authStore.user.name,
                };
            }
        } finally {
            savingGeneralInfo.value = false;
        }
    }

    async function fetchDevices() {
        devices.value = await getSessions();
    }

    async function signOutDevice(id: number) {
        const isCurrent = devices.value.find((d) => d.id === id)?.isCurrentDevice ?? false;
        signingOutDeviceId.value = id;
        try {
            await signOutSession(id);
            if (isCurrent) {
                await authStore.logoutUser();
                return;
            }
            devices.value = devices.value.filter((d) => d.id !== id);
        } catch (error) {
            const apiErr = ApiError.from(error);
            if (apiErr?.status === 401) {
                authStore.clearSession(true);
                return;
            }
            if (apiErr?.status === 404) {
                devices.value = await getSessions();
                return;
            }
            throw error;
        } finally {
            signingOutDeviceId.value = null;
        }
    }

    function resetForm() {
        if (!originalData.value) return;
        applyData(originalData.value);
        clearAvatarFile();
        avatarMarkedForDeletion.value = false;
    }

    return {
        form,
        originalData,
        loading,
        saving,
        savingGeneralInfo,
        devices,
        signingOutDeviceId,
        avatarFile,
        avatarMarkedForDeletion,
        displayAvatarUrl,
        isDirty,
        fullName,
        initialized,
        clearAvatarFile,
        markAvatarForDeletion,
        setAvatarFile,
        fetchAccountSettings,
        fetchDevices,
        saveAccountSettings,
        saveGeneralInformation,
        signOutDevice,
        resetForm,
    };
});
